import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import translations from "@/data/translations";
import { MessageCircle, X } from "lucide-react";

const WHATSAPP_BASE = "https://wa.me/34672808030";

const WhatsAppWidget = () => {
  const { lang } = useLang();
  const t = translations[lang].whatsappWidget;
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 3000);
    return () => clearTimeout(timer);
  }, []);

  const whatsappUrl = `${WHATSAPP_BASE}?text=${encodeURIComponent(t.prefilled)}`;

  if (!visible) return null;

  return (
    <div className="fixed bottom-20 right-5 z-[60] flex flex-col items-end gap-3">
      {/* Chat popup */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            transition={{ duration: 0.25 }}
            data-testid="whatsapp-popup"
            className="w-[300px] rounded-2xl overflow-hidden shadow-2xl shadow-black/20"
          >
            {/* Header */}
            <div className="bg-[#075E54] px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <img
                    src="/logo.png"
                    alt="OKontainers"
                    className="w-7 h-7 object-contain brightness-0 invert"
                  />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm font-heading">OKontainers</p>
                  <p className="text-emerald-200 text-xs font-body">Online</p>
                </div>
              </div>
              <button
                data-testid="whatsapp-close"
                onClick={() => setOpen(false)}
                className="text-white/70 hover:text-white transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="bg-[#ECE5DD] px-5 py-6">
              <div className="bg-white rounded-xl rounded-tl-none px-4 py-3 shadow-sm max-w-[85%]">
                <p className="text-slate-700 text-sm leading-relaxed font-body">
                  {t.greeting}
                </p>
                <p className="text-[10px] text-slate-400 mt-1 text-right font-body">
                  {new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </p>
              </div>
            </div>

            {/* CTA */}
            <div className="bg-white px-5 py-4 border-t border-slate-100">
              <a
                data-testid="whatsapp-widget-cta"
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full bg-[#25D366] text-white font-semibold py-3 rounded-full hover:bg-[#20BD5A] transition-all text-sm"
              >
                <MessageCircle size={18} />
                {t.cta}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating button */}
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 260, damping: 20, delay: 0.2 }}
        data-testid="whatsapp-widget-button"
        onClick={() => setOpen(!open)}
        className="w-14 h-14 rounded-full bg-[#25D366] text-white flex items-center justify-center shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/50 hover:scale-110 transition-all duration-300"
      >
        {open ? <X size={24} /> : <MessageCircle size={26} fill="white" />}
      </motion.button>
    </div>
  );
};

export default WhatsAppWidget;
